/**
 * Configurações > Operadoras de convênio (#811, clínica).
 *
 * ── Duas fontes, uma lista ───────────────────────────────────────────────
 *
 * As operadoras cadastradas aqui e as que o SISAC mantém aparecem juntas,
 * porque é da mesma lista que a recepção escolhe o convênio. As do SISAC
 * vêm marcadas e sem botão: a próxima sincronização desfaria a edição, e um
 * botão que a API recusa é uma porta que existe só para dizer não.
 *
 * ── Desativar, e não apagar ──────────────────────────────────────────────
 *
 * Operadora desativada sai das opções de convênio novo e continua nos
 * convênios que já a usam — como os motivos de perda.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Operadora } from '@cliqcrm/contracts';
import { useApi, useOperadoras } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, Cartao, Chip } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

export function SecaoOperadoras(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const operadoras = useOperadoras(true);
  const invalidar = () => void cache.invalidateQueries({ queryKey: chaves.operadoras() });

  const [nome, definirNome] = useState('');
  const [registro, definirRegistro] = useState('');
  const criar = useMutation({
    mutationFn: () =>
      api!.criarOperadora({
        corpo: { nome: nome.trim(), registro_ans: registro.trim() === '' ? null : registro.trim() },
      }),
    onSuccess: () => {
      definirNome('');
      definirRegistro('');
      invalidar();
    },
  });

  return (
    <div className="admin__funis-painel">
      <p className="admin__nota">
        As operadoras de convênio que a recepção escolhe no cadastro do paciente. As que vêm do
        SISAC são mantidas por ele e não se editam aqui. Uma operadora desativada sai da lista de
        escolha e continua nos convênios que já a usam.
      </p>

      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (nome.trim() !== '') criar.mutate();
        }}
      >
        <Campo
          id="operadora-nome"
          rotulo="Nova operadora"
          type="text"
          value={nome}
          maxLength={120}
          onChange={(e) => definirNome(e.currentTarget.value)}
        />
        <Campo
          id="operadora-registro"
          rotulo="Registro na ANS"
          type="text"
          inputMode="numeric"
          value={registro}
          maxLength={10}
          ajuda="Opcional. Seis dígitos."
          onChange={(e) => definirRegistro(e.currentTarget.value)}
        />
        {criar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(criar.error, { generica: 'Não foi possível cadastrar a operadora.' })}
          </Faixa>
        )}
        <span className="admin__form-acoes">
          <Botao
            type="submit"
            desabilitado={nome.trim() === '' || criar.isPending || api === null}
            motivo={criar.isPending ? 'Cadastrando…' : 'Escreva o nome antes.'}
          >
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
          </Botao>
        </span>
      </Cartao>

      {operadoras.isPending && <p>Buscando as operadoras…</p>}
      {operadoras.isError && <Faixa tom="bloqueado">Não foi possível buscar as operadoras.</Faixa>}
      {operadoras.data?.itens.length === 0 && (
        <p className="admin__nota">
          Nenhuma operadora ainda. Enquanto a lista estiver vazia, o convênio do paciente só pode
          ser particular.
        </p>
      )}

      {(operadoras.data?.itens.length ?? 0) > 0 && (
        <ul className="admin__motivos" aria-label="Operadoras de convênio">
          {operadoras.data!.itens.map((operadora) => (
            <LinhaDaOperadora key={operadora.id} operadora={operadora} aoMudar={invalidar} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LinhaDaOperadora({
  operadora,
  aoMudar,
}: {
  operadora: Operadora;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const [editando, definirEditando] = useState(false);
  const [nome, definirNome] = useState(operadora.nome);
  const [registro, definirRegistro] = useState(operadora.registro_ans ?? '');
  const doSisac = operadora.origem === 'agenda';

  const editar = useMutation({
    mutationFn: (mudanca: { nome: string; registro_ans: string | null; ativa: boolean }) =>
      api!.editarOperadora({ caminho: { id: operadora.id }, corpo: mudanca }),
    onSuccess: () => {
      definirEditando(false);
      aoMudar();
    },
  });

  const atual = {
    nome: operadora.nome,
    registro_ans: operadora.registro_ans,
    ativa: operadora.ativa,
  };

  return (
    <Cartao como="li" variante="linha" parte="admin__motivo">
      {editando ? (
        <form
          className="admin__motivo-edicao"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (nome.trim() !== '') {
              editar.mutate({
                ...atual,
                nome: nome.trim(),
                registro_ans: registro.trim() === '' ? null : registro.trim(),
              });
            }
          }}
        >
          <Campo
            rotulo={`Nome de "${operadora.nome}"`}
            type="text"
            value={nome}
            maxLength={120}
            autoFocus
            onChange={(e) => definirNome(e.currentTarget.value)}
          />
          <Campo
            rotulo={`Registro na ANS de "${operadora.nome}"`}
            type="text"
            inputMode="numeric"
            value={registro}
            maxLength={10}
            onChange={(e) => definirRegistro(e.currentTarget.value)}
          />
          <span className="admin__form-acoes">
            <Botao variante="fantasma" miudo onClick={() => definirEditando(false)}>
              Cancelar
            </Botao>
            <Botao
              type="submit"
              miudo
              desabilitado={nome.trim() === '' || editar.isPending}
              motivo="Salvando…"
            >
              Salvar
            </Botao>
          </span>
        </form>
      ) : (
        <>
          <span className="admin__motivo-texto">
            {operadora.nome}
            {operadora.registro_ans && ` · ANS ${operadora.registro_ans}`}
            {doSisac && <Chip title="Mantida pelo SISAC">do SISAC</Chip>}
            {!operadora.ativa && <Chip title="Não aparece para escolher">desativada</Chip>}
          </span>
          {!doSisac && (
            <span className="admin__form-acoes">
              <Botao
                miudo
                variante="fantasma"
                aria-label={`Editar ${operadora.nome}`}
                onClick={() => {
                  definirNome(operadora.nome);
                  definirRegistro(operadora.registro_ans ?? '');
                  definirEditando(true);
                }}
              >
                Editar
              </Botao>
              <Botao
                miudo
                variante="fantasma"
                desabilitado={editar.isPending}
                motivo="Salvando…"
                aria-label={`${operadora.ativa ? 'Desativar' : 'Reativar'} ${operadora.nome}`}
                onClick={() => editar.mutate({ ...atual, ativa: !operadora.ativa })}
              >
                {operadora.ativa ? 'Desativar' : 'Reativar'}
              </Botao>
            </span>
          )}
        </>
      )}
      {editar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(editar.error, { generica: 'Não foi possível salvar a operadora.' })}
        </Faixa>
      )}
    </Cartao>
  );
}
